// ─────────────────────────────────────────────────────────────────────
// useStagedAttachment — the staged-attachment model for the dashboard
// composer (TaskEricInput).
//
// A photo attached via the paperclip / drag / paste STAGES in the composer
// as a thumbnail and immediately kicks off a background classify call
// (`/api/eric/attach/classify-image`, which uploads to R2 + runs Sonnet
// vision). The thumbnail shows a calm "Reading photo…" state until the
// result lands; then content-aware action chips appear.
//
// This replaces the scattered `attach*` / `attachSuggestion` /
// `vinNoFocusOffer` state and the two-branch `onAttachFile` logic in
// TaskEricInput (a cold paperclip that jumped to an action surface, vs. a
// mid-conversation photo fired at Eric with no preview). One model now:
// stage → background-classify → chips → user taps a chip or hits Send.
//
// Lifecycle guarantees:
//   • Send HOLDS (≤~3s) for an in-flight classify instead of aborting it —
//     the extraction IS the dispatch payload now (pixels never ship to the
//     brain; locked 2026-06-10). The classify is aborted only on clear /
//     re-stage / unmount.
//   • A monotonic *staging token* drops stale results: if the user clears
//     and re-stages (or stages twice quickly) while a classify is in
//     flight, the older response is ignored even if it resolves last.
//   • The object URL is revoked on clear / re-stage / unmount so we never
//     leak blobs.
//
// `buildSuggestions` is a PURE function (no hooks, no I/O) so it's unit-
// testable and the render layer can re-derive chips from
// classification + focus without re-running the network.
// ─────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react'

import type { EricFocus } from '@/components/dashboard/agentic/types'
import { decodeVin, isValidVin, type DecodedVin } from '@/lib/vin'
import type { EricPhotoContext } from '@/lib/eric-stream'
import { downscaleImage } from '@/lib/image-downscale'

/**
 * Result of `/api/eric/attach/classify-image` — the photo's R2 key, what the
 * Sonnet-vision pass thinks it is, and the structured fields it pulled. The
 * canonical shape lives here (the staged-attachment model is its only consumer
 * now that the old EricAttachPreview card is gone).
 */
export type ImageClassification = {
  photo_r2_key: string
  kind: 'vin_sticker' | 'odometer' | 'receipt' | 'other'
  confidence: 'high' | 'medium' | 'low'
  summary: string
  extracted: {
    vin: string | null
    mileage_km: number | null
    total_cents: number | null
    currency: string | null
  }
  parse_error?: boolean
}

export type StagedStatus = 'analyzing' | 'ready' | 'error'

/**
 * A content-aware action the staged photo offers. Each suggestion is one
 * chip in the composer. `tone` drives visual weight (the recommended action
 * is `primary`, alternatives are `quiet`). `kind` is what the chip *does* —
 * the render layer maps it to the matching handler.
 */
export type AttachmentSuggestion =
  | {
      kind: 'add_vehicle'
      tone: 'primary' | 'quiet'
      /** "Add {Year Make Model} to {Customer}" — built at render via t(). */
      label: string
      vin: string
      year: number | null
      make: string | null
      model: string | null
      vehicleLabel: string
      /** Resolved owner from the conversation, when present. */
      customerId: string | null
      customerName: string | null
    }
  | {
      kind: 'pick_customer'
      // Usually a quiet alternative, but PRIMARY when it leads — e.g. a cold
      // composer with typed text, where we suppress the recency-guessed owner
      // and make the mechanic pick explicitly.
      tone: 'primary' | 'quiet'
      vin: string
      year: number | null
      make: string | null
      model: string | null
      vehicleLabel: string
    }
  | {
      kind: 'save_orphan'
      tone: 'quiet'
      vin: string
      year: number | null
      make: string | null
      model: string | null
      vehicleLabel: string
    }
  | {
      kind: 'log_mileage'
      tone: 'primary' | 'quiet'
      mileageKm: number
      photoR2Key: string
      /** Focus vehicle, when the conversation resolved one. */
      vehicleId: string | null
      vehicleLabel: string | null
    }
  | {
      kind: 'pick_vehicle'
      tone: 'primary' | 'quiet'
      mileageKm: number
      photoR2Key: string
    }
  | {
      kind: 'save_receipt'
      tone: 'primary' | 'quiet'
      photoR2Key: string
      totalCents: number | null
      currency: string | null
      customerId: string | null
      customerName: string | null
    }
  | {
      kind: 'ask_eric'
      tone: 'primary' | 'quiet'
    }

export type StagedAttachment = {
  file: File
  previewUrl: string
  status: StagedStatus
  classification: ImageClassification | null
  /** A decoded VIN (valid 17-char + NHTSA-resolved) when the photo is a VIN
   *  sticker — carried so suggestion labels can show Year/Make/Model. */
  decodedVin: DecodedVin | null
}

/** What `buildSuggestions` reads about the surrounding conversation. */
export type SuggestionContext = {
  /** The customer/vehicle Eric resolved this session, or null. */
  focus: EricFocus | null
  /** Shop's most-recently-active customer (no-focus fallback owner). */
  recentCustomer: { id: string; name: string } | null
  /** True when there's a live conversation (a reply card up, or a resolved
   *  customer in focus). In that case the brain should decide create-vs-update
   *  an existing vehicle — so we LEAD with "Ask Eric" and demote the
   *  deterministic create-chips. */
  isInConversation: boolean
  /** True when the composer already holds typed text. With text present the
   *  mechanic is composing an explicit instruction (which Send routes to the
   *  brain, binding the car to the customer NAMED in the text) — so we must NOT
   *  pre-fill a recency-GUESSED owner on the add-vehicle chip (that mis-attributed
   *  a walk-in's car to the wrong "most recent" customer). Empty composer + no
   *  focus is the pure-photo convenience case where the recency guess is fine. */
  composerHasComposeText: boolean
}

function vehicleLabelOf(decoded: DecodedVin | null, vin: string): string {
  if (!decoded) return vin
  const parts = [decoded.year, decoded.make, decoded.model]
    .filter(Boolean)
    .join(' ')
    .trim()
  return parts || vin
}

/**
 * Pure: derive the content-aware chip set from a classification + context.
 *
 * Rules (mirror the plan's Target model):
 *   • Never auto-act — these are *offers*; nothing writes until tapped/Sent.
 *   • Deterministic create-chips are gated to the COLD path. In an active
 *     conversation lead with "Ask Eric" (the brain decides create-vs-update,
 *     avoiding a dup vehicle), and demote the create-chips to `quiet`.
 *   • vin_sticker (decodes) → "Add {veh} to {customer}" + Pick customer / Save orphan.
 *   • odometer (mileage_km) → "Log {N} km on {vehicle}" + Pick vehicle.
 *   • receipt (total)       → "Save receipt to {customer}".
 *   • other / low-conf      → no deterministic chip.
 *   • ALWAYS → "Ask Eric".
 *
 * `classification` may be null (still analyzing / no result yet) — returns
 * just the "Ask Eric" escape hatch so the row is never empty.
 */
export function buildSuggestions(
  classification: ImageClassification | null,
  decodedVin: DecodedVin | null,
  ctx: SuggestionContext,
): AttachmentSuggestion[] {
  const askEricTone: 'primary' | 'quiet' = 'primary'
  const askEric: AttachmentSuggestion = { kind: 'ask_eric', tone: askEricTone }

  if (!classification) return [askEric]

  const inConvo = ctx.isInConversation
  // In conversation, "Ask Eric" leads (primary); cold, the deterministic
  // create-chip leads and "Ask Eric" is the quieter escape hatch.
  const askEricFinal: AttachmentSuggestion = {
    kind: 'ask_eric',
    tone: inConvo ? 'primary' : 'quiet',
  }
  const detPrimary: 'primary' | 'quiet' = inConvo ? 'quiet' : 'primary'

  const out: AttachmentSuggestion[] = []

  // VIN sticker → add-vehicle path (needs a clean decode to be deterministic).
  if (classification.kind === 'vin_sticker' && decodedVin) {
    const vin = classification.extracted.vin
    if (vin) {
      const vehicleLabel = vehicleLabelOf(decodedVin, vin)
      // Recency-guessed owner ONLY on a cold, no-text staging (the pure-photo
      // convenience case). The moment the composer carries typed text the
      // mechanic is composing an explicit instruction — Send routes it to the
      // brain, which binds the car to the customer NAMED in the text — so a
      // prominent "Add {veh} to {recent}" risks the wrong owner (the walk-in
      // mis-attributed to "Mobik"). Suppress the guess: lead with Pick customer,
      // offer Save-orphan + Ask Eric, and drop the pre-owned add-chip.
      if (!ctx.focus?.customerId && ctx.composerHasComposeText) {
        out.push({
          kind: 'pick_customer',
          tone: detPrimary,
          vin,
          year: decodedVin.year,
          make: decodedVin.make,
          model: decodedVin.model,
          vehicleLabel,
        })
        out.push({
          kind: 'save_orphan',
          tone: 'quiet',
          vin,
          year: decodedVin.year,
          make: decodedVin.make,
          model: decodedVin.model,
          vehicleLabel,
        })
        out.push(askEricFinal)
        return out
      }
      const ownerId = ctx.focus?.customerId ?? ctx.recentCustomer?.id ?? null
      const ownerName =
        ctx.focus?.customerId
          ? ctx.focus.customerName
          : (ctx.recentCustomer?.name ?? null)
      out.push({
        kind: 'add_vehicle',
        tone: detPrimary,
        label: vehicleLabel,
        vin,
        year: decodedVin.year,
        make: decodedVin.make,
        model: decodedVin.model,
        vehicleLabel,
        customerId: ownerId,
        customerName: ownerName,
      })
      out.push({
        kind: 'pick_customer',
        tone: 'quiet',
        vin,
        year: decodedVin.year,
        make: decodedVin.make,
        model: decodedVin.model,
        vehicleLabel,
      })
      out.push({
        kind: 'save_orphan',
        tone: 'quiet',
        vin,
        year: decodedVin.year,
        make: decodedVin.make,
        model: decodedVin.model,
        vehicleLabel,
      })
      out.push(askEricFinal)
      return out
    }
  }

  // Odometer → log-mileage path.
  if (
    classification.kind === 'odometer' &&
    classification.confidence !== 'low' &&
    classification.extracted.mileage_km !== null
  ) {
    const vehicleId = ctx.focus?.vehicleId ?? null
    const vehicleLabel = ctx.focus?.vehicleLabel ?? null
    if (vehicleId) {
      out.push({
        kind: 'log_mileage',
        tone: detPrimary,
        mileageKm: classification.extracted.mileage_km,
        photoR2Key: classification.photo_r2_key,
        vehicleId,
        vehicleLabel,
      })
    } else {
      // No vehicle in focus → must pick one first.
      out.push({
        kind: 'pick_vehicle',
        tone: detPrimary,
        mileageKm: classification.extracted.mileage_km,
        photoR2Key: classification.photo_r2_key,
      })
    }
    out.push(askEricFinal)
    return out
  }

  // Receipt → save-attachment path.
  if (classification.kind === 'receipt' && classification.confidence !== 'low') {
    const customerId = ctx.focus?.customerId ?? ctx.recentCustomer?.id ?? null
    const customerName = ctx.focus?.customerId
      ? ctx.focus.customerName
      : (ctx.recentCustomer?.name ?? null)
    if (customerId) {
      out.push({
        kind: 'save_receipt',
        tone: detPrimary,
        photoR2Key: classification.photo_r2_key,
        totalCents: classification.extracted.total_cents,
        currency: classification.extracted.currency,
        customerId,
        customerName,
      })
    }
    out.push(askEricFinal)
    return out
  }

  // other / low-confidence / undecoded VIN → only "Ask Eric".
  return [askEric]
}

/** Confidence gate: a VIN counts as deterministic only if it's a valid
 *  17-char VIN AND decodes via NHTSA (year/make/model resolved). Mirrors
 *  the old `tryDecodeVin` in TaskEricInput. */
async function tryDecodeVin(vin: string): Promise<DecodedVin | null> {
  const v = vin.trim().toUpperCase()
  if (!isValidVin(v)) return null
  try {
    const d = await decodeVin(v)
    return d && (d.year || d.make || d.model) ? d : null
  } catch {
    return null
  }
}

/** Outcome of consuming the staged photo on Send. */
export type ConsumeResult =
  | { kind: 'ready'; photo: EricPhotoContext }
  /** Classify failed / unparseable — the error chip explains; user retakes. */
  | { kind: 'unreadable' }
  /** Still analyzing after the hold — composer hints; user re-sends in a beat. */
  | { kind: 'pending' }
  | { kind: 'none' }

export type UseStagedAttachment = {
  staged: StagedAttachment | null
  /** Stage a file: thumbnail in immediately, classify in the background. */
  stage: (file: File) => void
  /** Clear the staged photo + abort any in-flight classify + revoke the URL. */
  clear: () => void
  /**
   * Consume the staged photo for a Send-to-Eric dispatch. Holds up to `holdMs`
   * for an in-flight classify (the analysis started at attach time), then
   * returns its extraction as the dispatch payload — pixels never ship. Only
   * `ready` tears down the staged state: `unreadable` and `pending` leave the
   * photo staged so the user can retake or re-send.
   */
  consumeForSend: (holdMs?: number) => Promise<ConsumeResult>
}

/**
 * Hook owning a single staged attachment. `getToken` is the Clerk token
 * accessor (the classify call is auth-gated). `onError` lets the host
 * surface a toast/inline error; the hook also flips `status` to `'error'`.
 */
export function useStagedAttachment(opts: {
  getToken: () => Promise<string | null>
  onError?: (message: string) => void
}): UseStagedAttachment {
  const { getToken, onError } = opts
  const [staged, setStaged] = useState<StagedAttachment | null>(null)

  // The live object URL — kept in a ref so cleanup never closes over a stale
  // value (and so unmount can revoke it without a dependency).
  const previewUrlRef = useRef<string | null>(null)
  // In-flight classify controller — aborted on clear / re-stage / Send.
  const abortRef = useRef<AbortController | null>(null)
  // Monotonic token: only the result whose token still matches `tokenRef`
  // is allowed to settle. Bumped on every stage/clear so stale responses
  // are dropped even if they resolve after a newer stage.
  const tokenRef = useRef(0)
  // Latest staged file, so consumeForSend reads the current file without a
  // render-cycle race.
  const fileRef = useRef<File | null>(null)
  // R2 key from the classify upload (eric-attach already PUT the photo to
  // VIN_PHOTOS). Null until classify settles (or after clear/re-stage).
  const r2KeyRef = useRef<string | null>(null)
  // Settled classify state mirrored into refs so consumeForSend reads current
  // values without a render-cycle race (same reason fileRef exists), plus the
  // in-flight classify promise so Send can HOLD for it.
  const statusRef = useRef<StagedStatus | null>(null)
  const classificationRef = useRef<ImageClassification | null>(null)
  const decodedVinRef = useRef<DecodedVin | null>(null)
  const classifyDoneRef = useRef<Promise<void> | null>(null)

  const revoke = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }, [])

  const teardown = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    revoke()
    fileRef.current = null
    r2KeyRef.current = null
    statusRef.current = null
    classificationRef.current = null
    decodedVinRef.current = null
    classifyDoneRef.current = null
    tokenRef.current += 1
  }, [revoke])

  const clear = useCallback(() => {
    teardown()
    setStaged(null)
  }, [teardown])

  const stage = useCallback(
    (file: File) => {
      if (!file) return
      // Supersede any prior staged photo + in-flight classify.
      abortRef.current?.abort()
      revoke()
      const token = ++tokenRef.current
      const isCurrent = () => tokenRef.current === token

      const previewUrl = URL.createObjectURL(file)
      previewUrlRef.current = previewUrl
      fileRef.current = file
      r2KeyRef.current = null
      statusRef.current = 'analyzing'
      classificationRef.current = null
      decodedVinRef.current = null
      setStaged({
        file,
        previewUrl,
        status: 'analyzing',
        classification: null,
        decodedVin: null,
      })

      const ac = new AbortController()
      abortRef.current = ac

      classifyDoneRef.current = (async () => {
        try {
          // Downscale before upload (≤1600px JPEG): the classify lane is the
          // only reader of the photo now, Anthropic caps images at ~5MB
          // decoded, and Android shots run 3-12MB. Failure-safe — any decode
          // problem returns the original file and classify rejects it honestly.
          const uploadFile = await downscaleImage(file)
          if (!isCurrent()) return
          const fd = new FormData()
          fd.append('photo', uploadFile)
          const authToken = await getToken()
          if (!authToken) throw new Error('missing_token')
          const res = await fetch('/api/eric/attach/classify-image', {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
            body: fd,
            signal: ac.signal,
          })
          if (!res.ok) throw new Error(`status_${res.status}`)
          const data = (await res.json()) as ImageClassification
          if (!isCurrent()) return
          // A parse_error response (HTTP 200, but Sonnet's vision JSON didn't
          // parse) is a FAILED classify — surface the error state, not a false
          // 'ready'. Otherwise the chip shows "Photo attached" + an "Ask Eric"
          // chip while consumeForSend maps parse_error to 'unreadable', which no
          // caller dispatches: Send + chip would silently no-op forever (breaks
          // the "classify failure = honest hint" contract).
          if (data.parse_error) {
            statusRef.current = 'error'
            onError?.('attach_failed')
            setStaged((prev) =>
              prev && prev.file === file ? { ...prev, status: 'error' } : prev,
            )
            return
          }
          // The classify call already uploaded the photo to VIN_PHOTOS — the key
          // rides the photo context so audit/thread layers can reference it.
          r2KeyRef.current = data.photo_r2_key
          classificationRef.current = data

          // A clean VIN decode upgrades the classification to a deterministic
          // add-vehicle suggestion. Decode off the classify result so the
          // chip can show Year/Make/Model, not the raw VIN.
          let decodedVin: DecodedVin | null = null
          if (data.kind === 'vin_sticker' && data.extracted.vin) {
            decodedVin = await tryDecodeVin(data.extracted.vin)
            if (!isCurrent()) return
          }
          decodedVinRef.current = decodedVin
          statusRef.current = 'ready'

          setStaged((prev) =>
            prev && prev.file === file
              ? { ...prev, status: 'ready', classification: data, decodedVin }
              : prev,
          )
        } catch (err) {
          if ((err as { name?: string } | null)?.name === 'AbortError') return
          if (!isCurrent()) return
          statusRef.current = 'error'
          console.error(
            JSON.stringify({
              event: 'staged_attachment.classify_failed',
              error: err instanceof Error ? err.message : String(err),
            }),
          )
          onError?.('attach_failed')
          setStaged((prev) =>
            prev && prev.file === file ? { ...prev, status: 'error' } : prev,
          )
        }
      })()
    },
    [getToken, onError, revoke],
  )

  const consumeForSend = useCallback(
    async (holdMs = 3000): Promise<ConsumeResult> => {
      // Capture the staging token so the hold is a token-guarded settle path
      // like the classify IIFE: a clear / re-stage / unmount during the hold
      // bumps tokenRef, and we must not consume a DIFFERENT photo's refs (or a
      // torn-down one) under the Send the user initiated for this photo.
      const token = tokenRef.current
      if (!fileRef.current) return { kind: 'none' }
      // HOLD for the in-flight classify (locked 2026-06-10): the analysis
      // started at attach time and usually settles before the user finishes
      // typing. It is NOT aborted on Send anymore — its extraction IS the
      // dispatch payload; pixels never ship to the brain.
      if (statusRef.current === 'analyzing' && classifyDoneRef.current) {
        await Promise.race([
          classifyDoneRef.current,
          new Promise((resolve) => setTimeout(resolve, holdMs)),
        ])
      }
      // A clear / re-stage / unmount during the hold superseded this photo —
      // the refs now belong to a different (or no) photo. Don't dispatch it.
      if (tokenRef.current !== token || !fileRef.current) return { kind: 'none' }
      if (statusRef.current !== 'ready') {
        return statusRef.current === 'error' ? { kind: 'unreadable' } : { kind: 'pending' }
      }
      const cls = classificationRef.current
      if (!cls || cls.parse_error) return { kind: 'unreadable' }
      const decodedVin = decodedVinRef.current
      const photo: EricPhotoContext = {
        r2_key: cls.photo_r2_key,
        kind: cls.kind,
        confidence: cls.confidence,
        summary: cls.summary,
        extracted: cls.extracted,
        decoded: decodedVin
          ? {
              year: decodedVin.year ?? null,
              make: decodedVin.make ?? null,
              model: decodedVin.model ?? null,
            }
          : null,
      }
      teardown()
      setStaged(null)
      return { kind: 'ready', photo }
    },
    [teardown],
  )

  // Revoke the object URL + abort on unmount, and invalidate any in-flight
  // settle. The classify fetch is aborted, but the NHTSA decode (tryDecodeVin)
  // takes no abort signal — without bumping the token a late decode after
  // unmount would flip statusRef to 'ready' and let a held consumeForSend
  // dispatch an Eric turn from the dead component (invisible until reload).
  // Kept dependency-free (refs only) so it stays a true mount/unmount effect.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
      tokenRef.current += 1
      fileRef.current = null
      statusRef.current = null
    }
  }, [])

  return { staged, stage, clear, consumeForSend }
}
